import { styled, Typography } from 'opensolar-ui'
import React from 'react'
import { RecommendationItem, RecommendationsList } from '../shared/RecommendationsList'

interface InverterRecommendationsListProps {
  inverterSets: any[]
  selectedIndex: number | null
  onSelect: (index: number) => void
}

const TooltipContainer = styled('div')({
  padding: '8px 0',
  minWidth: '200px',
})

const TooltipTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '10px',
  lineHeight: '20px',
})

const TooltipRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  fontSize: '13px',
  lineHeight: '18px',
})

const TooltipLabel = styled('span')({
  color: '#6B7280',
  fontWeight: 400,
})

const TooltipValue = styled('span')({
  color: '#111827',
  fontWeight: 500,
  marginLeft: '12px',
})

const buildTooltipContent = (
  inverterSet: any,
  inverterNames: string,
  combinedRating: number | undefined,
  quantity: number,
  index: number
) => {
  const systemDcAcRatio = inverterSet.system_dc_ac_ratio
  const firstComponent = inverterSet.components?.[0]
  const efficiency = firstComponent?.efficiency || firstComponent?.data?.efficiency
  const mpptQuantity = firstComponent?.mppt_quantity || firstComponent?.data?.mppt_quantity
  const phaseType = firstComponent?.phase_type || firstComponent?.data?.phase_type
  const maxPowerRating = firstComponent?.max_power_rating || firstComponent?.data?.max_power_rating

  return (
    <TooltipContainer>
      <TooltipTitle>{inverterNames || `Option ${index + 1}`}</TooltipTitle>
      {combinedRating && (
        <TooltipRow>
          <TooltipLabel>Combined Rating</TooltipLabel>
          <TooltipValue>{combinedRating}kW</TooltipValue>
        </TooltipRow>
      )}
      {systemDcAcRatio && (
        <TooltipRow>
          <TooltipLabel>DC/AC Ratio</TooltipLabel>
          <TooltipValue>{systemDcAcRatio.toFixed(2)}</TooltipValue>
        </TooltipRow>
      )}
      {quantity > 0 && (
        <TooltipRow>
          <TooltipLabel>Quantity</TooltipLabel>
          <TooltipValue>{quantity}</TooltipValue>
        </TooltipRow>
      )}
      {maxPowerRating && quantity === 1 && (
        <TooltipRow>
          <TooltipLabel>Rating</TooltipLabel>
          <TooltipValue>{maxPowerRating}kW</TooltipValue>
        </TooltipRow>
      )}
      {efficiency && (
        <TooltipRow>
          <TooltipLabel>Efficiency</TooltipLabel>
          <TooltipValue>{efficiency}%</TooltipValue>
        </TooltipRow>
      )}
      {mpptQuantity && (
        <TooltipRow>
          <TooltipLabel>MPPTs</TooltipLabel>
          <TooltipValue>{mpptQuantity}</TooltipValue>
        </TooltipRow>
      )}
      {phaseType && (
        <TooltipRow>
          <TooltipLabel>Phase</TooltipLabel>
          <TooltipValue>
            {phaseType === 'single_phase' ? 'Single' : phaseType === 'three_phase' ? 'Three' : phaseType}
          </TooltipValue>
        </TooltipRow>
      )}
    </TooltipContainer>
  )
}

export const InverterRecommendationsList: React.FC<InverterRecommendationsListProps> = ({
  inverterSets,
  selectedIndex,
  onSelect,
}) => {
  const items: RecommendationItem[] = inverterSets.map((inverterSet: any, index: number) => {
    // Get the first component for display (or combine if multiple)
    const firstComponent = inverterSet.components?.[0]
    const code = firstComponent?.name || firstComponent?.code || `Option ${index + 1}`
    const manufacturer = firstComponent?.manufacturer_name || firstComponent?.manufacturer || undefined

    // For tooltip, show all components
    const inverterNames = inverterSet.components
      ?.map((c: any) => {
        const mfr = c.manufacturer_name || c.manufacturer
        const compCode = c.name || c.code
        return mfr && compCode ? `${mfr} ${compCode}` : compCode || mfr || 'Unknown'
      })
      .filter(Boolean)
      .join(', ')
    const combinedRating = inverterSet.combined_rating
    const quantity = inverterSet.components?.reduce((sum: number, c: any) => sum + (c.quantity || 1), 0) || 0

    const tooltipContent = buildTooltipContent(inverterSet, inverterNames, combinedRating, quantity, index)

    return {
      title: code,
      manufacturer,
      quantity,
      headlineFigure: combinedRating ? { value: combinedRating, unit: 'kW' } : undefined,
      tooltipContent,
    }
  })

  return <RecommendationsList items={items} selectedIndex={selectedIndex} onSelect={onSelect} maxDisplay={3} />
}
